<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Shipping</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage shipping rates and options</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <span
                                                v-if="hasUnsavedChanges"
                                                class="text-xs bg-amber-100 text-amber-700 py-1 px-2 rounded-full font-medium"
                                        >
                                                Unsaved changes
                                        </span>
                                        <button
                                                type="button"
                                                :disabled="!hasUnsavedChanges || isSaving"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-green-600 hover:bg-green-700 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="saveAllChanges"
                                        >
                                                <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isTableLoading"
                                                @click="refreshData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isTableLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="openCreateModal"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create New
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search by offering..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <label class="text-sm text-gray-500 whitespace-nowrap">Country:</label>
                                                <select
                                                        v-model="selectedCountry"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-32"
                                                        @change="applyFilters"
                                                >
                                                        <option value="">All Countries</option>
                                                        <option v-for="country in countries" :key="country.iso" :value="country.iso">
                                                                {{ country.name }}
                                                        </option>
                                                </select>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <label class="text-sm text-gray-500 whitespace-nowrap">Currency:</label>
                                                <select
                                                        v-model="selectedCurrency"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-32"
                                                        @change="applyFilters"
                                                >
                                                        <option value="">All Currencies</option>
                                                        <option v-for="curr in currencies" :key="curr.iso" :value="curr.iso">
                                                                {{ curr.iso }}
                                                        </option>
                                                </select>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <label class="text-sm text-gray-500 whitespace-nowrap">In Store:</label>
                                                <select
                                                        v-model="selectedInstore"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-32"
                                                        @change="applyFilters"
                                                >
                                                        <option value="">All</option>
                                                        <option value="1">Yes</option>
                                                        <option value="0">No</option>
                                                </select>
                                        </div>
                                        <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                {{ totalRows }} rates
                                        </span>
                                </div>
                        </div>

                        <!-- Table Card -->
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Table -->
                                <div class="overflow-x-auto">
                                        <!-- Loading State -->
                                        <div v-if="isTableLoading" class="flex justify-center items-center py-16 px-4">
                                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span class="ml-3 text-gray-500 text-sm">Loading shipping rates...</span>
                                        </div>

                                        <!-- Empty State -->
                                        <div v-else-if="filteredData.length === 0" class="text-center py-16 px-4">
                                                <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                </svg>
                                                <h3 class="text-base font-medium text-gray-700 m-0 mb-1">No shipping rates found</h3>
                                                <p class="text-sm text-gray-500 m-0">Try adjusting your filters or create a new shipping rate.</p>
                                        </div>

                                        <!-- Data Table -->
                                        <table v-else class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Offering</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Currency</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Country</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sale</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Scope</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Days</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Tracking</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Express</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">In Store</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr
                                                                v-for="item in paginatedData"
                                                                :key="item.id"
                                                                class="border-b border-gray-200 transition-colors"
                                                                :class="isRowChanged(item.id) ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'"
                                                        >
                                                                <td class="py-3.5 px-4 text-sm text-gray-700">{{ item.offeringid }}</td>
                                                                <td class="py-3.5 px-4 text-sm text-gray-700 font-medium">{{ item.currency }}</td>
                                                                <td class="py-3.5 px-4 text-sm text-gray-700">
                                                                        <span v-if="item.countrycode">{{ getCountryName(item.countrycode) }}</span>
                                                                        <span v-else class="text-gray-400">All Countries</span>
                                                                </td>
                                                                <td class="py-3.5 px-4">
                                                                        <input
                                                                                v-model="item.name"
                                                                                type="text"
                                                                                class="py-1.5 px-2 border border-transparent rounded-md text-sm w-full max-w-40 outline-none bg-transparent transition-colors focus:border-orange-7 focus:bg-white"
                                                                                @input="markRowChanged(item.id)"
                                                                        >
                                                                </td>
                                                                <td class="py-3.5 px-4">
                                                                        <input
                                                                                v-model.number="item.sale"
                                                                                type="number"
                                                                                class="py-1.5 px-2 border border-transparent rounded-md text-sm w-20 outline-none bg-transparent transition-colors focus:border-orange-7 focus:bg-white"
                                                                                @input="markRowChanged(item.id)"
                                                                        >
                                                                </td>
                                                                <td class="py-3.5 px-4">
                                                                        <select
                                                                                v-model="item.scope"
                                                                                class="py-1.5 px-2 border border-transparent rounded-md text-sm outline-none bg-transparent transition-colors cursor-pointer focus:border-orange-7 focus:bg-white"
                                                                                @change="markRowChanged(item.id)"
                                                                        >
                                                                                <option v-for="opt in scopeOptions" :key="opt" :value="opt">{{ opt }}</option>
                                                                        </select>
                                                                </td>
                                                                <td class="py-3.5 px-4 text-sm text-gray-700">
                                                                        <div class="flex items-center gap-1">
                                                                                <input
                                                                                        v-model.number="item.days_min"
                                                                                        type="number"
                                                                                        class="py-1 px-1 border border-transparent rounded text-sm w-12 text-center outline-none bg-transparent focus:border-orange-7 focus:bg-white"
                                                                                        @input="markRowChanged(item.id)"
                                                                                >
                                                                                <span class="text-gray-400">-</span>
                                                                                <input
                                                                                        v-model.number="item.days_max"
                                                                                        type="number"
                                                                                        class="py-1 px-1 border border-transparent rounded text-sm w-12 text-center outline-none bg-transparent focus:border-orange-7 focus:bg-white"
                                                                                        @input="markRowChanged(item.id)"
                                                                                >
                                                                        </div>
                                                                </td>
                                                                <td class="py-3.5 px-4 text-center">
                                                                        <button
                                                                                type="button"
                                                                                class="w-10 h-6 rounded-full border-none cursor-pointer relative transition-colors"
                                                                                :class="item.tracking ? 'bg-orange-7' : 'bg-gray-300'"
                                                                                @click="toggleField(item, 'tracking')"
                                                                        >
                                                                                <span
                                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow"
                                                                                        :class="item.tracking ? 'left-[1.125rem]' : 'left-0.5'"
                                                                                ></span>
                                                                        </button>
                                                                </td>
                                                                <td class="py-3.5 px-4 text-center">
                                                                        <button
                                                                                type="button"
                                                                                class="w-10 h-6 rounded-full border-none cursor-pointer relative transition-colors"
                                                                                :class="item.express ? 'bg-orange-7' : 'bg-gray-300'"
                                                                                @click="toggleField(item, 'express')"
                                                                        >
                                                                                <span
                                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow"
                                                                                        :class="item.express ? 'left-[1.125rem]' : 'left-0.5'"
                                                                                ></span>
                                                                        </button>
                                                                </td>
                                                                <td class="py-3.5 px-4 text-center">
                                                                        <button
                                                                                type="button"
                                                                                class="w-10 h-6 rounded-full border-none cursor-pointer relative transition-colors"
                                                                                :class="item.instore ? 'bg-orange-7' : 'bg-gray-300'"
                                                                                @click="toggleInstore(item)"
                                                                        >
                                                                                <span
                                                                                        class="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow"
                                                                                        :class="item.instore ? 'left-[1.125rem]' : 'left-0.5'"
                                                                                ></span>
                                                                        </button>
                                                                </td>
                                                                <td class="py-3.5 px-4 text-right">
                                                                        <button
                                                                                type="button"
                                                                                class="p-1.5 border-none bg-transparent text-red-500 cursor-pointer rounded-md transition-colors hover:bg-red-50"
                                                                                title="Delete"
                                                                                @click="deleteShipping(item)"
                                                                        >
                                                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                        </button>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="py-4 px-6 border-t border-gray-200 flex justify-between items-center flex-wrap gap-4">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredData.length) }} of {{ filteredData.length }} results
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                                        @click="currentPage > 1 && currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                                        @click="currentPage < totalPages && currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div class="fixed inset-0 bg-black/50" @click="closeCreateModal"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                                <div class="p-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create Shipping Rate</h3>
                                </div>
                                <div class="p-6 flex flex-col gap-4">
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Offering ID *</label>
                                                <input
                                                        v-model.number="newShipping.offeringid"
                                                        type="number"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                        placeholder="Enter offering ID"
                                                >
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                                <select
                                                        v-model="newShipping.countrycode"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                >
                                                        <option value="">All Countries</option>
                                                        <option v-for="country in countries" :key="country.iso" :value="country.iso">
                                                                {{ country.name }}
                                                        </option>
                                                </select>
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                                                <select
                                                        v-model="newShipping.currency"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                >
                                                        <option value="">Select currency</option>
                                                        <option v-for="curr in currencies" :key="curr.iso" :value="curr.iso">
                                                                {{ curr.iso }} — {{ curr.name }}
                                                        </option>
                                                </select>
                                        </div>
                                        <div>
                                                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                <input
                                                        v-model="newShipping.name"
                                                        type="text"
                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                        placeholder="Enter shipping name"
                                                >
                                        </div>
                                        <div class="grid grid-cols-2 gap-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
                                                        <input
                                                                v-model.number="newShipping.sale"
                                                                type="number"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                placeholder="0"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                                                        <select
                                                                v-model="newShipping.scope"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                        >
                                                                <option v-for="opt in scopeOptions" :key="opt" :value="opt">{{ opt }}</option>
                                                        </select>
                                                </div>
                                        </div>
                                        <div class="grid grid-cols-2 gap-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Min Days</label>
                                                        <input
                                                                v-model.number="newShipping.days_min"
                                                                type="number"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                placeholder="1"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Max Days</label>
                                                        <input
                                                                v-model.number="newShipping.days_max"
                                                                type="number"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                placeholder="5"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex gap-4">
                                                <label class="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                                v-model="newShipping.tracking"
                                                                type="checkbox"
                                                                class="w-4 h-4 accent-orange-7"
                                                        >
                                                        <span class="text-sm text-gray-700">Tracking</span>
                                                </label>
                                                <label class="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                                v-model="newShipping.express"
                                                                type="checkbox"
                                                                class="w-4 h-4 accent-orange-7"
                                                        >
                                                        <span class="text-sm text-gray-700">Express</span>
                                                </label>
                                                <label class="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                                v-model="newShipping.instore"
                                                                type="checkbox"
                                                                class="w-4 h-4 accent-orange-7"
                                                        >
                                                        <span class="text-sm text-gray-700">In Store</span>
                                                </label>
                                        </div>
                                </div>
                                <div class="p-4 px-6 border-t border-gray-200 flex justify-end gap-3">
                                        <button
                                                type="button"
                                                class="py-2 px-4 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors"
                                                @click="closeCreateModal"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="isCreating"
                                                class="py-2 px-4 border-none bg-orange-7 rounded-lg text-sm font-medium text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-6 transition-colors"
                                                @click="createShipping"
                                        >
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import ShippingService from 'services/ShippingService';
import CountryService from 'services/CountryService';
import CurrencyService from 'services/CurrencyService';
import { ShippingModel, CountryModel, CurrencyModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import { confirmDelete } from 'utils/swal';

interface ShippingRow extends Omit<ShippingModel, 'tracking' | 'express'> {
        tracking: boolean | number;
        express: boolean | number;
}

export default defineComponent({
        name: 'ShippingPage',
        setup() {
                const shippingData = ref<ShippingRow[]>([]);
                const countries = ref<CountryModel[]>([]);
                const currencies = ref<CurrencyModel[]>([]);
                const isTableLoading = ref(true);
                const searchQuery = ref('');
                const selectedCountry = ref('');
                const selectedCurrency = ref('');
                const selectedInstore = ref('');
                const currentPage = ref(1);
                const pageSize = 20;
                const showCreateModal = ref(false);
                const isCreating = ref(false);
                const isSaving = ref(false);
                const changedRowIds = ref<Set<number>>(new Set());

                const scopeOptions = ['quantity', 'item', 'variant', 'type', 'group', 'order'];

                const newShipping = ref({
                        offeringid: null as number | null,
                        countrycode: '',
                        currency: '',
                        name: '',
                        sale: 0,
                        scope: 'order' as ShippingModel['scope'],
                        days_min: 1,
                        days_max: 5,
                        tracking: false,
                        express: false,
                        instore: false
                });

                const totalRows = computed(() => filteredData.value.length);

                const filteredData = computed(() => {
                        let data = shippingData.value;

                        if (selectedCountry.value) {
                                data = data.filter(item => item.countrycode === selectedCountry.value);
                        }

                        if (selectedCurrency.value) {
                                data = data.filter(item => item.currency === selectedCurrency.value);
                        }

                        if (selectedInstore.value !== '') {
                                const instoreVal = Number(selectedInstore.value);
                                data = data.filter(item => item.instore === instoreVal);
                        }

                        if (searchQuery.value) {
                                const query = searchQuery.value.toLowerCase();
                                data = data.filter(item =>
                                        String(item.offeringid).includes(query) ||
                                        (item.name && item.name.toLowerCase().includes(query))
                                );
                        }

                        return data;
                });

                const totalPages = computed(() => Math.ceil(filteredData.value.length / pageSize));

                const paginatedData = computed(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredData.value.slice(start, start + pageSize);
                });

                const getCountryName = (iso: string) => {
                        const country = countries.value.find(c => c.iso === iso);
                        return country ? country.name : iso;
                };

                const loadData = async () => {
                        isTableLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const [shippingResponse, countriesResponse, currenciesResponse] = await Promise.all([
                                        ShippingService.getAll(params),
                                        CountryService.getAll(params),
                                        CurrencyService.getAll(params)
                                ]);
                                shippingData.value = (shippingResponse as ShippingRow[]).map(item => ({
                                        ...item,
                                        tracking: Boolean(item.tracking),
                                        express: Boolean(item.express)
                                }));
                                countries.value = countriesResponse as CountryModel[];
                                currencies.value = currenciesResponse as CurrencyModel[];
                                changedRowIds.value = new Set();
                        } catch (error) {
                                console.error('Failed to load data:', error);
                        } finally {
                                isTableLoading.value = false;
                        }
                };

                const refreshData = () => {
                        loadData();
                };

                const applyFilters = () => {
                        currentPage.value = 1;
                };

                const hasUnsavedChanges = computed(() => changedRowIds.value.size > 0);

                const markRowChanged = (id: number) => {
                        changedRowIds.value = new Set(changedRowIds.value).add(id);
                };

                const isRowChanged = (id: number) => changedRowIds.value.has(id);

                const toggleField = (item: ShippingRow, field: 'tracking' | 'express') => {
                        item[field] = !item[field];
                        markRowChanged(item.id);
                };

                const toggleInstore = (item: ShippingRow) => {
                        item.instore = item.instore ? 0 : 1;
                        markRowChanged(item.id);
                };

                const saveAllChanges = async () => {
                        if (!hasUnsavedChanges.value) return;
                        isSaving.value = true;
                        try {
                                const promises = shippingData.value
                                        .filter(item => changedRowIds.value.has(item.id))
                                        .map(item => ShippingService.update(item.id, {
                                                name: item.name,
                                                sale: item.sale,
                                                scope: item.scope,
                                                days_min: item.days_min,
                                                days_max: item.days_max,
                                                tracking: item.tracking ? 1 : 0,
                                                express: item.express ? 1 : 0,
                                                instore: item.instore ? 1 : 0
                                        }));
                                await Promise.all(promises);
                                changedRowIds.value = new Set();
                                Swal.fire({ icon: 'success', title: 'Saved', text: 'All changes have been saved.', timer: 1500, showConfirmButton: false });
                        } catch (error) {
                                console.error('Failed to save changes:', error);
                                Swal.fire({ icon: 'error', title: 'Save Failed', text: 'Some changes could not be saved. Please try again.' });
                        } finally {
                                isSaving.value = false;
                        }
                };

                const openCreateModal = () => {
                        newShipping.value = {
                                offeringid: null,
                                currency: '',
                                countrycode: '',
                                name: '',
                                sale: 0,
                                scope: 'order',
                                days_min: 1,
                                days_max: 5,
                                tracking: false,
                                express: false,
                                instore: false
                        };
                        showCreateModal.value = true;
                };

                const closeCreateModal = () => {
                        showCreateModal.value = false;
                };

                const createShipping = async () => {
                        if (newShipping.value.offeringid === null || isNaN(Number(newShipping.value.offeringid))) {
                                Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Offering ID is required and must be a valid number.' });
                                return;
                        }
                        if (!newShipping.value.currency) {
                                Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Currency is required.' });
                                return;
                        }

                        isCreating.value = true;
                        try {
                                const payload = {
                                        ...newShipping.value,
                                        countrycode: newShipping.value.countrycode || null,
                                        tracking: newShipping.value.tracking ? 1 : 0,
                                        express: newShipping.value.express ? 1 : 0,
                                        instore: newShipping.value.instore ? 1 : 0
                                };
                                await ShippingService.create(payload as unknown as ShippingModel);
                                closeCreateModal();
                                await loadData();
                        } catch (error) {
                                console.error('Failed to create shipping:', error);
                                Swal.fire({ icon: 'error', title: 'Save Failed', text: 'Could not create the shipping rate. Please try again.' });
                        } finally {
                                isCreating.value = false;
                        }
                };

                const deleteShipping = async (item: ShippingRow) => {
                        const result = await confirmDelete({
                                title: 'Delete Shipping Rate',
                                text: `Are you sure you want to delete this shipping rate for offering ${item.offeringid}?`
                        });
                        const confirmed = result.isConfirmed;

                        if (confirmed) {
                                try {
                                        await ShippingService.delete(item.id);
                                        shippingData.value = shippingData.value.filter(s => s.id !== item.id);
                                        const updated = new Set(changedRowIds.value);
                                        updated.delete(item.id);
                                        changedRowIds.value = updated;
                                } catch (error) {
                                        console.error('Failed to delete shipping:', error);
                                        Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete the shipping rate. Please try again.' });
                                }
                        }
                };

                onMounted(() => {
                        loadData();
                });

                onBeforeRouteLeave(async (_to, _from, next) => {
                        if (hasUnsavedChanges.value) {
                                const result = await Swal.fire({
                                        title: 'Unsaved Changes',
                                        text: 'You have unsaved changes. Are you sure you want to leave this page?',
                                        icon: 'warning',
                                        showCancelButton: true,
                                        confirmButtonColor: '#FF6600',
                                        cancelButtonColor: '#6b7280',
                                        confirmButtonText: 'Leave Page',
                                        cancelButtonText: 'Stay',
                                });
                                if (result.isConfirmed) {
                                        next();
                                } else {
                                        next(false);
                                }
                        } else {
                                next();
                        }
                });

                return {
                        shippingData,
                        countries,
                        currencies,
                        isTableLoading,
                        searchQuery,
                        selectedCountry,
                        selectedCurrency,
                        selectedInstore,
                        currentPage,
                        pageSize,
                        totalRows,
                        filteredData,
                        totalPages,
                        paginatedData,
                        showCreateModal,
                        isCreating,
                        isSaving,
                        hasUnsavedChanges,
                        newShipping,
                        scopeOptions,
                        getCountryName,
                        refreshData,
                        applyFilters,
                        markRowChanged,
                        isRowChanged,
                        toggleField,
                        toggleInstore,
                        saveAllChanges,
                        openCreateModal,
                        closeCreateModal,
                        createShipping,
                        deleteShipping
                };
        }
});
</script>

<style scoped>
@keyframes spin {
        from {
                transform: rotate(0deg);
        }
        to {
                transform: rotate(360deg);
        }
}
</style>
